import {
  messagePathDataType,
  MessagePathNode,
  pathMatcher,
  TopicNode,
} from "@/shared/domain/topics";
import type { TopicData } from "@/shared/state/visualization";
import type { Axes } from "@/shared/state/visualization/schema/v1/three-d";

export type AxisName = "forward" | "right" | "up";

export interface PreservedAxis {
  axis: AxisName;
  node: MessagePathNode;
}

/**
 * Given a component's current Axes (Position, VectorField) and the topic
 * tree for the file the user has just switched to, returns each axis whose
 * existing path still resolves in the new file, paired with the matched
 * MessagePathNode for the caller to convert to TopicData. Axes that do not
 * resolve are absent from the result; the caller resets them to 0.
 *
 * An axis resolves when the new file holds a node under the axis's topic name
 * whose message path parts line up with the axis's own, and whose canonical
 * data type equals the data type of the axis's last attribute part. Slice
 * bounds are copied onto the matched node rather than compared, so an axis
 * saved as `foo[2]` resolves against a node spelling itself `foo[:]` and comes
 * back carrying `[2]`.
 *
 * Pure function. No dispatches, no `.toTopicData()` calls (which can throw on
 * topics without a default representation). The caller owns those concerns.
 */
export function reconcileAxisPathsToFile(
  axes: Axes | undefined,
  newFileTopicTree: TopicNode[],
): PreservedAxis[] {
  if (!axes) {
    return [];
  }

  const entries: Array<[AxisName, TopicData | number]> = [
    ["forward", axes.forward],
    ["right", axes.right],
    ["up", axes.up],
  ];

  const preserved: PreservedAxis[] = [];
  for (const [axis, value] of entries) {
    if (typeof value === "number") {
      continue;
    }
    const node = matchAxisInTree(value, newFileTopicTree);
    if (node) {
      preserved.push({ axis, node });
    }
  }
  return preserved;
}

function matchAxisInTree(
  axis: TopicData,
  topicTree: TopicNode[],
): MessagePathNode | undefined {
  const dataType = messagePathDataType(axis.messagePath.parts);

  return pathMatcher
    .findNodesByParts(axis.topic.name, axis.messagePath.parts, topicTree)
    .find((node) => node.data.canonical_data_type === dataType);
}
